import { IStage } from '@forwardflow/store/onboardingStoreV2'
import {
   IAvailabilityDay,
   ICampaignConfiguration,
   IStageMapGroup,
   ITempStageGroup,
   TOnboardingStep,
} from '@forwardflow/types/onboarding'

export const initialAvailabilityDays: IAvailabilityDay[] = [
   {
      day: 'Monday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Tuesday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Wednesday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Thursday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Friday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Saturday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: true,
   },
   {
      day: 'Sunday',
      startTime: '09:00',
      endTime: '18:00',
      enabled: false,
   },
]

export const OPERATOR_EMAILS = new Set(['operator@forward-flow.com'])

export const ONBOARDING_DUMMY_ORGANIZATION_ID =
   'f5f26377cff4a4ed9b3b07ba7c4e8a18'

export const AGENT_ONBOARDING_STEPS: TOnboardingStep[] = [
   'calendar-connection',
   'getting-started',
]

/**
 * The setup wizard: seven steps, under three minutes of the customer's time,
 * then straight to the dashboard where the first campaign is written.
 *
 * The Follow Up Boss key comes first because it is what lets every later
 * screen open pre-filled: the team (and so the seat cost), the area code,
 * the stop rules and the business details all come from the connected
 * account. The customer confirms; the wizard does not ask twice.
 */
export const ONBOARDING_STEPS: TOnboardingStep[] = [
   'fub-connect',
   'you-and-team',
   'budget',
   'payment',
   'area-code',
   'your-number',
   'stop-rules',
   'business-details',
]

/**
 * How the steps are shown in the left-hand rail: budget and payment are two
 * screens of one step, so the customer sees seven, not eight.
 */
export interface IRailStep {
   id: string
   title: string
   /** What the step will ask, shown before it is reached. */
   ask: string
   steps: TOnboardingStep[]
}

export const ONBOARDING_RAIL: IRailStep[] = [
   {
      id: 'connect',
      title: 'Connect Follow Up Boss',
      ask: 'Paste one API key',
      steps: ['fub-connect'],
   },
   {
      id: 'you',
      title: 'You and your team',
      ask: 'Confirm who gets the leads',
      steps: ['you-and-team'],
   },
   {
      id: 'plan',
      title: 'Budget and payment',
      ask: 'Pick a monthly budget, add a card',
      steps: ['budget', 'payment'],
   },
   {
      id: 'area',
      title: 'Your calling area code',
      ask: 'Confirm the area code leads see',
      steps: ['area-code'],
   },
   {
      id: 'number',
      title: 'Your Forward Flow number',
      ask: 'Save the number that hands you leads',
      steps: ['your-number'],
   },
   {
      id: 'stops',
      title: 'Leads we leave alone',
      ask: 'Confirm three short lists',
      steps: ['stop-rules'],
   },
   {
      id: 'business',
      title: 'Business details',
      ask: 'Fill the blanks so texting can be approved',
      steps: ['business-details'],
   },
]

/**
 * Steps retired from the wizard (2026-10-12 and 2026-10-14); components kept
 * on disk. A saved record that still points at one of these is moved to the
 * step that replaced it, see `LEGACY_STEP_MAP`.
 */
export const RETIRED_ONBOARDING_STEPS: TOnboardingStep[] = [
   'plan-selection',
   'payment-details',
   'team-invitation',
   'phone-number-configuration',
   'stage-mapping-explained',
   'calendar-connection',
   'leads-availability',
   'user-availability',
   'presentation-step',
   'setup-cname',
   'summary-before-stage-mapping',
   'dnd-stage-mapping',
   'campaign-configuration',
   'voice-agent-configuration',
   'install-inbox-app',
   'budget-and-card',
   'number-and-stops',
   'first-campaign',
   'voice',
   'start-calling',
]

/** Where a record saved on a retired step resumes. */
export const LEGACY_STEP_MAP: Partial<Record<string, TOnboardingStep>> = {
   'plan-selection': 'fub-connect',
   payment: 'payment',
   'payment-details': 'payment',
   'budget-and-card': 'budget',
   'team-invitation': 'you-and-team',
   'phone-number-configuration': 'area-code',
   'number-and-stops': 'area-code',
   'stage-mapping-explained': 'stop-rules',
   'dnd-stage-mapping': 'stop-rules',
   'stage-mapping-stop-stages': 'stop-rules',
   'campaign-configuration': 'business-details',
   'voice-agent-configuration': 'business-details',
   'first-campaign': 'business-details',
   voice: 'business-details',
   'start-calling': 'business-details',
}

/**
 * The step a loaded record resumes on. Retired steps map to their
 * replacement. Anything else passes through unchanged, an unknown step
 * included: its meaning is not guessed, the wizard says the step is
 * unavailable instead. An empty step starts at the first one.
 */
export const normalizeOnboardingStep = (
   step: string | null | undefined,
   isAgent = false
): TOnboardingStep => {
   if (!step) return (isAgent ? AGENT_ONBOARDING_STEPS : ONBOARDING_STEPS)[0]
   return LEGACY_STEP_MAP[step] || (step as TOnboardingStep)
}

/**
 * The customer's own time per step, in seconds, for the "about n min left"
 * line in the header. Reading and tapping only; server work runs behind the
 * next screen.
 */
export const STEP_SECONDS: Partial<Record<TOnboardingStep, number>> = {
   'fub-connect': 25,
   'you-and-team': 20,
   budget: 15,
   payment: 35,
   'area-code': 10,
   'your-number': 15,
   'stop-rules': 25,
   'business-details': 30,
}

export const STEP_LABELS: Record<TOnboardingStep, string> = {
   budget: 'Budget',
   payment: 'Payment',
   'area-code': 'Area code',
   'your-number': 'Your number',
   'stop-rules': 'Leads we leave alone',
   'you-and-team': 'You and your team',
   'budget-and-card': 'Budget and card',
   'number-and-stops': 'Number and stop rules',
   'first-campaign': 'First campaign',
   voice: 'Voice',
   'start-calling': 'Start calling',
   'assistant-name': 'Assistant Name',
   'plan-selection': 'Plan Selection',
   'fub-connect': 'Follow Up Boss',
   'payment-details': 'Payment Details',
   'team-invitation': 'Team Invitation',
   'leads-availability': 'Leads Availability',
   'user-availability': 'User Availability',
   'business-details': 'Business details',
   'presentation-step': 'Presentation Step',
   'setup-cname': 'Setup CNAM',
   'summary-before-stage-mapping': 'Summary Before Stage Mapping',
   'stage-mapping-nurture-stages': 'Stage Mapping Nurture Stages',
   'stage-mapping-appointment': 'Stage Mapping Appointment',
   'stage-mapping-stop-stages': 'Stage Mapping Stop Stages',
   'stage-mapping-definition': 'Stage Mapping Definition',
   'stage-mapping-new-leads': 'Stage Mapping New Leads',
   'campaign-configuration': 'Campaign Setup',
   'voice-agent-configuration': 'Voice Agent',
   'phone-number-configuration': 'Phone Number Selection',
   'install-inbox-app': 'Install Inbox App',
   'dnd-stage-mapping': 'Stage Mapping',
   'stage-mapping-explained': 'Stage Mapping',
   'calendar-connection': 'Calendar Connection',
   'getting-started': 'Getting Started',
}

const EMPTY_STAGE: IStage = {
   id: '',
   name: '',
   description: '',
   type: 'stage',
}

export const initialTempStageGroups: ITempStageGroup[] = [
   {
      groupId: 'ff-nurturing',
      parentGroupId: null,
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-appointment-set',
      parentGroupId: null,
      type: 'appointment-field',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-optional-mappings',
      parentGroupId: null,
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-appointment-missed',
      parentGroupId: null,
      type: 'no-criteria',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-do-not-contact',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-active-client',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-under-contract',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-closed-client',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-has-agent',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
   {
      groupId: 'ff-other-stop',
      parentGroupId: 'ff-stopped',
      stageOrTags: [EMPTY_STAGE],
   },
]

export const initialStageMappingsGroups: IStageMapGroup[] = [
   {
      group: 'ff-nurturing',
      timeline: '0-3 Months',
      isMandatory: true,
      allowMultiple: false,
      stages: [],
      tags: [],
   },
   {
      type: 'appointment-field',
      group: 'ff-appointment-set',
      stages: [],
      isMandatory: true,
      tags: [],
      allowMultiple: true,
   },
   {
      group: 'ff-appointment-missed',
      stages: [],
      tags: [],
      allowMultiple: false,
   },
   {
      group: 'ff-stopped',
      types: [
         {
            key: 'ff-active-client',
            stages: [],
            tags: [],
            allowMultiple: true,
         },
         {
            key: 'ff-under-contract',
            stages: [],
            tags: [],
            isMandatory: true,
            allowMultiple: true,
         },
         {
            key: 'ff-closed-client',
            stages: [],
            tags: [],
            isMandatory: true,
            allowMultiple: false,
         },
         {
            key: 'ff-do-not-contact',
            stages: [],
            tags: [],
            isMandatory: true,
            allowMultiple: false,
         },

         {
            key: 'ff-has-agent',
            stages: [],
            tags: [],
            allowMultiple: false,
         },
         {
            key: 'ff-other-stop',
            stages: [],
            tags: [],
            allowMultiple: true,
         },
      ],
   },
]

export const initialCampaignConfiguration: ICampaignConfiguration = {
   usps: null,
   source: '',
   status: 'draft',
   ai_assistants: [],
   campaign_name: '',
   phone_numbers: [],
   campaign_filters: {
      eligible_criteria: [],
   },
   first_to_contact: {
      buffer_time: 0,
   },
   is_demo_campaign: null,
   qualifying_questions: null,
   appointment_timeframes: null,
   discovery_people_limit: 0,
   area_codes: [],
   lead_type_filter: ['buyer', 'seller'],
}

// A partial list of U.S. area codes (not exhaustive):
export const US_AREA_CODES = [
   '201',
   '202',
   '203',
   '205',
   '206',
   '207',
   '208',
   '209',
   '210',
   '212',
   '213',
   '214',
   '215',
   '216',
   '217',
   '218',
   '219',
   '224',
   '225',
   '228',
   '229',
   '231',
   '234',
   '239',
   '240',
   '248',
   '251',
   '252',
   '253',
   '254',
   '256',
   '260',
   '262',
   '267',
   '269',
   '270',
   '272',
   '274',
   '276',
   '281',
   '301',
   '302',
   '303',
   '304',
   '305',
   '307',
   '308',
   '309',
   '310',
   '312',
   '313',
   '314',
   '315',
   '316',
   '317',
   '318',
   '319',
   '320',
   '321',
   '323',
   '325',
   '327',
   '330',
   '331',
   '334',
   '336',
   '337',
   '339',
   '346',
   '347',
   '351',
   '352',
   '360',
   '361',
   '364',
   '380',
   '385',
   '386',
   '401',
   '402',
   '404',
   '405',
   '406',
   '407',
   '408',
   '409',
   '410',
   '412',
   '413',
   '414',
   '415',
   '417',
   '419',
   '423',
   '424',
   '425',
   '430',
   '432',
   '434',
   '435',
   '440',
   '442',
   '443',
   '447',
   '458',
   '463',
   '464',
   '470',
   '475',
   '478',
   '479',
   '480',
   '484',
   '501',
   '502',
   '503',
   '504',
   '505',
   '507',
   '508',
   '509',
   '510',
   '512',
   '513',
   '515',
   '516',
   '517',
   '518',
   '520',
   '530',
   '531',
   '534',
   '539',
   '540',
   '541',
   '551',
   '559',
   '561',
   '562',
   '563',
   '564',
   '567',
   '570',
   '571',
   '573',
   '574',
   '575',
   '580',
   '585',
   '586',
   '601',
   '602',
   '603',
   '605',
   '606',
   '607',
   '608',
   '609',
   '610',
   '612',
   '614',
   '615',
   '616',
   '617',
   '618',
   '619',
   '620',
   '623',
   '626',
   '628',
   '629',
   '630',
   '631',
   '636',
   '641',
   '646',
   '650',
   '651',
   '657',
   '660',
   '661',
   '662',
   '667',
   '669',
   '678',
   '679',
   '681',
   '682',
   '701',
   '702',
   '703',
   '704',
   '706',
   '707',
   '708',
   '712',
   '713',
   '714',
   '715',
   '716',
   '717',
   '718',
   '719',
   '720',
   '724',
   '725',
   '727',
   '730',
   '731',
   '732',
   '734',
   '737',
   '740',
   '747',
   '754',
   '757',
   '760',
   '762',
   '763',
   '765',
   '769',
   '770',
   '772',
   '773',
   '774',
   '775',
   '779',
   '781',
   '785',
   '786',
   '801',
   '802',
   '803',
   '804',
   '805',
   '806',
   '808',
   '810',
   '812',
   '813',
   '814',
   '815',
   '816',
   '817',
   '818',
   '828',
   '830',
   '831',
   '832',
   '838',
   '839',
   '840',
   '843',
   '845',
   '847',
   '848',
   '850',
   '854',
   '856',
   '857',
   '858',
   '859',
   '860',
   '862',
   '863',
   '864',
   '865',
   '870',
   '872',
   '878',
   '901',
   '903',
   '904',
   '906',
   '907',
   '908',
   '909',
   '910',
   '912',
   '913',
   '914',
   '915',
   '916',
   '917',
   '918',
   '919',
   '920',
   '925',
   '928',
   '929',
   '930',
   '931',
   '934',
   '936',
   '937',
   '938',
   '940',
   '941',
   '945',
   '947',
   '949',
   '951',
   '952',
   '954',
   '956',
   '959',
   '970',
   '971',
   '972',
   '973',
   '975',
   '978',
   '979',
   '980',
   '984',
   '985',
   '989',
]

export const EAC_NEW_LEAD = 0.62
export const EAC_COLD_LEAD = 0.44
export const BASE_SEAT_COST = 97
export const ADDITIONAL_SEAT_COST = 50
export const MIN_TOP_UP = 200
